import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import { nextStep, type OrderFeedRow } from '@zilian/contracts';
import { api } from '../api';
import StatementDialog, { type Candidate } from '../StatementDialog';
import { Empty, Heading, money, RowAction, Tag, type Props, type StatusTone } from '../ui';

type Side = 'SELL' | 'BUY';

interface Partner {
  id: string;
  name: string;
  /** Not on the platform: recorded and reconciled by this enterprise alone. */
  local: boolean;
  unbilled: number;
  count: number;
  pending: number;
  open: number;
  /** A local unit's orders still to reconcile or settle. */
  orders: string[];
}

/** Where a statement stands and whose turn it is, from this enterprise's side. */
export function statementStep(s: any, workspace: string): { label: string; tone: StatusTone } {
  const selling = s.seller_id === workspace;
  const issuedByMe = (s.issued_by_seller ? s.seller_id : s.buyer_id) === workspace;
  if (s.status === 'PENDING')
    return issuedByMe
      ? { label: '等对方确认', tone: 'info' }
      : { label: '待我确认', tone: 'warning' };
  if (s.status === 'REJECTED') return { label: '已退回', tone: 'neutral' };
  if (s.status === 'WITHDRAWN') return { label: '已撤回', tone: 'neutral' };
  const left = Number(s.total) - Number(s.paid);
  if (left <= 0) return { label: '已结清', tone: 'success' };
  if (Number(s.claimed) > 0)
    return selling
      ? { label: '待确认收款', tone: 'warning' }
      : { label: '等对方确认收款', tone: 'info' };
  return selling ? { label: '等待付款', tone: 'info' } : { label: '待付款', tone: 'warning' };
}

/** A statement for a unit not on the platform: in effect once issued, so only money is open. */
export function localStatementStep(s: any): { label: string; tone: StatusTone } {
  if (s.status === 'WITHDRAWN') return { label: '已撤回', tone: 'neutral' };
  if (s.link?.answer === 'DISPUTED') return { label: '对方有异议', tone: 'warning' };
  if (Number(s.paid) >= Number(s.total)) return { label: '已结清', tone: 'success' };
  return s.kind === 'SALE'
    ? { label: '待收款', tone: 'warning' }
    : { label: '待付款', tone: 'warning' };
}

type Overview = { statements: any[]; unbilled: any[] };

/** A statement's next step when it waits on this enterprise; otherwise the row is only opened. */
const statementAction: Record<string, string> = {
  待我确认: '去确认',
  待确认收款: '确认收款',
  待付款: '去付款',
  待收款: '去收款',
  对方有异议: '去处理',
};

export function Statements(p: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [local, setLocal] = useState<Overview | null>(null);
  const [side, setSide] = useState<Side>('SELL');
  const [issuing, setIssuing] = useState<{ partnerId?: string } | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all([api<Overview>('/statements'), api<Overview>('/local-statements')])
      .then(([shared, own]) => {
        if (!live) return;
        setData(shared);
        setLocal(own);
      })
      .catch((e) => p.setError(e.message));
    return () => {
      live = false;
    };
  }, [p.version, p.workspace]);

  const selling = side === 'SELL';
  const peerOf = (row: any) =>
    selling
      ? { id: row.buyer_id, name: row.buyer_name }
      : { id: row.seller_id, name: row.seller_name };
  const mine = (row: any) => (selling ? row.seller_id : row.buyer_id) === p.workspace;

  const kind = selling ? 'SALE' : 'PURCHASE';
  // One row per partner: what is not yet billed, what is being confirmed, what is still owed.
  // Units not on the platform are listed too: their statements take effect once issued, so they
  // have nothing being confirmed.
  const partners = useMemo(() => {
    const map = new Map<string, Partner>();
    const entry = (peer: { id: string; name: string }, local = false) => {
      if (!map.has(peer.id))
        map.set(peer.id, {
          ...peer,
          local,
          unbilled: 0,
          count: 0,
          pending: 0,
          open: 0,
          orders: [],
        });
      return map.get(peer.id)!;
    };
    for (const t of (data?.unbilled ?? []).filter(mine)) {
      const e = entry(peerOf(t));
      e.unbilled += Number(t.proposed_amount ?? t.kept_amount);
      e.count += 1;
    }
    for (const s of (data?.statements ?? []).filter(mine)) {
      const e = entry(peerOf(s));
      if (s.status === 'PENDING') e.pending += Number(s.total);
      if (s.status === 'CONFIRMED') e.open += Number(s.total) - Number(s.paid);
    }
    for (const o of (local?.unbilled ?? []).filter((o) => o.kind === kind)) {
      const e = entry({ id: o.counterparty_id, name: o.counterparty }, true);
      e.unbilled += Number(o.amount);
      e.count += 1;
    }
    // Reconciled, alone or on a statement, and not yet fully paid: the 待收款 / 待付款 stage the
    // order lists show.
    for (const o of p.feed as (OrderFeedRow & { counterparty_id: string })[]) {
      if (o.source !== 'LOCAL' || o.side !== side) continue;
      const action = nextStep(o).action;
      if (action !== 'COLLECT' && action !== 'PAY') continue;
      const e = entry({ id: o.counterparty_id, name: o.peer }, true);
      e.open += Number(o.settlement_amount) - Number(o.paid ?? 0);
      e.orders.push(o.id);
    }
    return [...map.values()].sort((x, y) => y.unbilled + y.open - (x.unbilled + x.open));
  }, [data, local, p.feed, side, p.workspace]);
  const candidates: Candidate[] = [
    ...(data?.unbilled ?? []).filter(mine).map((t) => ({
      ...t,
      partnerId: peerOf(t).id,
      partnerName: peerOf(t).name,
      local: false,
      amount: t.proposed_amount ?? t.kept_amount,
    })),
    ...(local?.unbilled ?? [])
      .filter((o) => o.kind === kind)
      .map((o) => ({
        ...o,
        partnerId: o.counterparty_id,
        partnerName: o.counterparty,
        local: true,
      })),
  ];
  // A local unit's work is on its orders: the one order itself, or the list narrowed to it.
  const openLocal = (row: Partner) =>
    row.orders.length === 1
      ? p.openLocal(row.orders[0])
      : navigate(
          `/${selling ? 'sales' : 'purchases'}?view=MONEY&q=${encodeURIComponent(row.name)}`,
        );
  // Both kinds in one list, newest first; a local one is marked and opens its own page.
  const statements = [
    ...(data?.statements ?? []).filter(mine).map((s) => ({ ...s, peer: peerOf(s).name })),
    ...(local?.statements ?? [])
      .filter((s) => s.kind === kind)
      .map((s) => ({ ...s, peer: s.counterparty, local: true })),
  ].sort((x, y) => y.created_at.localeCompare(x.created_at));
  const sum = (key: 'unbilled' | 'pending' | 'open') =>
    partners.reduce((total, row) => total + row[key], 0);

  if (!data || !local)
    return (
      <Box sx={{ p: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  const verb = selling ? '收' : '付';
  return (
    <>
      <Heading
        title="对账结算"
        subtitle="按合作方汇总待对账和待收付款。合作企业的对账单需双方确认；本企业登记的单位发出即生效，可发给对方确认。款项线下支付，平台只记录。"
        // Offered once some partner has delivered orders to bill.
        action={
          partners.some((row) => row.count > 0) && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setIssuing({})}>
              生成对账单
            </Button>
          )
        }
      />
      <Tabs value={side} onChange={(_, v) => setSide(v)} sx={{ mb: 2 }}>
        <Tab value="SELL" label="应收 · 我是卖方" />
        <Tab value="BUY" label="应付 · 我是买方" />
      </Tabs>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {[
          ['待对账', sum('unbilled'), '已交付、还没对账的订单'],
          ['对账中', sum('pending'), '已发出、等待确认的对账单'],
          [`待${verb}款`, sum('open'), '已确认、尚未结清的金额'],
        ].map(([label, value, hint]) => (
          <Paper key={String(label)} sx={{ p: 2.5 }}>
            <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, mt: 0.5 }}>{money(value)}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 11.5, mt: 0.5 }}>
              {hint}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <div className="panel-title">
          <Typography sx={{ fontSize: 15, fontWeight: 650 }}>按合作方</Typography>
        </div>
        {partners.length ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{selling ? '客户' : '供应商'}</TableCell>
                  <TableCell align="right">待对账</TableCell>
                  <TableCell align="right">对账中</TableCell>
                  <TableCell align="right">待{verb}款</TableCell>
                  <TableCell align="right" sx={{ width: 140 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {partners.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{row.name}</Typography>
                        {row.local && <Tag tone="neutral">本企业登记</Tag>}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {row.count ? (
                        <>
                          {money(row.unbilled)}
                          <Typography
                            component="span"
                            color="text.secondary"
                            sx={{ fontSize: 12, ml: 0.75 }}
                          >
                            {row.count} 单
                          </Typography>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {row.pending ? money(row.pending) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {row.open > 0 ? money(row.open) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {/* A partner row has no page of its own: only its next step, if any. Money
                          still open on orders reconciled one by one is collected from them. */}
                      <RowAction
                        view={false}
                        label={
                          row.count > 0
                            ? '生成对账单'
                            : row.local && row.open > 0
                              ? `去${verb}款`
                              : undefined
                        }
                        onClick={() =>
                          row.count > 0 ? setIssuing({ partnerId: row.id }) : openLocal(row)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Empty>
            {selling ? '没有待对账或待收款的销售订单。' : '没有待对账或待付款的采购订单。'}
          </Empty>
        )}
      </Paper>

      <Paper>
        <div className="panel-title">
          <Typography sx={{ fontSize: 15, fontWeight: 650 }}>对账单</Typography>
        </div>
        {statements.length ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                  <TableCell>对账单</TableCell>
                  <TableCell>月份</TableCell>
                  <TableCell align="right">订单</TableCell>
                  <TableCell align="right">金额</TableCell>
                  <TableCell align="right">已{verb}</TableCell>
                  <TableCell>状态</TableCell>
                  <TableCell align="right" sx={{ width: 140 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {statements.map((s) => {
                  const step = s.local ? localStatementStep(s) : statementStep(s, p.workspace);
                  return (
                    <TableRow
                      key={s.id}
                      hover
                      onClick={() =>
                        navigate(s.local ? `/statements/local/${s.id}` : `/statements/${s.id}`)
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{s.peer}</Typography>
                          {s.local && <Tag tone="neutral">本企业登记</Tag>}
                        </Box>
                        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                          {s.number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {s.period.replace('-', ' 年 ')} 月
                      </TableCell>
                      <TableCell align="right">{s.line_count} 单</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {money(s.total)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {s.status === 'CONFIRMED' || (s.local && s.status === 'ISSUED')
                          ? money(s.paid)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Tag tone={step.tone}>{step.label}</Tag>
                      </TableCell>
                      <TableCell align="right">
                        <RowAction
                          label={statementAction[step.label]}
                          onClick={() =>
                            navigate(s.local ? `/statements/local/${s.id}` : `/statements/${s.id}`)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Empty>还没有对账单。选择合作方，把已交付的订单生成一张对账单。</Empty>
        )}
      </Paper>

      {issuing && (
        <StatementDialog
          side={side}
          candidates={candidates}
          partnerId={issuing.partnerId}
          onClose={() => setIssuing(null)}
          onDone={(id, isLocal) => {
            setIssuing(null);
            p.reload();
            p.notify(isLocal ? '对账单已生效，可发给对方确认' : '对账单已发出，等待对方确认');
            navigate(isLocal ? `/statements/local/${id}` : `/statements/${id}`);
          }}
        />
      )}
    </>
  );
}
